import { useMemo } from "react";
import {
  EntityFilterBar,
  FilterCategory,
} from "../../../../components/audit/EntityFilterBar";
import {
  ConfiguredState,
  isConfiguredState,
} from "../../../../models/audit/control";
import { WorkStepFilterOptions } from "../../../../models/audit/workStep";

interface WorkStepsFilterBarProps {
  searchTerm: string;
  onSearchTermChange: (value: string) => void;
  configuredStateFilters: ConfiguredState[];
  onConfiguredStateFiltersChange: (value: ConfiguredState[]) => void;
  sectionFilters: string[];
  onSectionFiltersChange: (value: string[]) => void;
  preparerFilters: string[];
  onPreparerFiltersChange: (value: string[]) => void;
  opsAuditFilters: string[];
  onOpsAuditFiltersChange: (value: string[]) => void;
  filterOptions: WorkStepFilterOptions | null | undefined;
  isFiltersOpen: boolean;
  onFiltersOpenChange: (open: boolean) => void;
  activeFilterCount: number;
  hasActiveFilters: boolean;
  onClearFilters: () => void;
}

export const WorkStepsFilterBar = ({
  searchTerm,
  onSearchTermChange,
  configuredStateFilters,
  onConfiguredStateFiltersChange,
  sectionFilters,
  onSectionFiltersChange,
  preparerFilters,
  onPreparerFiltersChange,
  opsAuditFilters,
  onOpsAuditFiltersChange,
  filterOptions,
  isFiltersOpen,
  onFiltersOpenChange,
  activeFilterCount,
  hasActiveFilters,
  onClearFilters,
}: WorkStepsFilterBarProps) => {
  const filterCategories: FilterCategory[] = useMemo(() => {
    const stateOptions = [
      { label: "Configured", value: ConfiguredState.Configured },
      { label: "Not Configured", value: ConfiguredState.NotConfigured },
      { label: "Draft", value: ConfiguredState.Draft },
    ];

    const sectionOptions =
      filterOptions?.sections.map((section) => ({
        label: section,
        value: section,
      })) ?? [];

    const preparerOptions =
      filterOptions?.preparers.map((preparer) => ({
        label: preparer,
        value: preparer,
      })) ?? [];

    const opsAuditOptions =
      filterOptions?.opsAudits.map((opsAudit) => ({
        label: opsAudit.name,
        value: opsAudit.id,
      })) ?? [];

    return [
      {
        id: "state",
        label: "State",
        options: stateOptions,
        selectedValues: configuredStateFilters,
        onSelectionChange: (values) =>
          onConfiguredStateFiltersChange(values.filter(isConfiguredState)),
      },
      {
        id: "section",
        label: "Section",
        options: sectionOptions,
        selectedValues: sectionFilters,
        onSelectionChange: onSectionFiltersChange,
      },
      {
        id: "preparer",
        label: "Preparer",
        options: preparerOptions,
        selectedValues: preparerFilters,
        onSelectionChange: onPreparerFiltersChange,
      },
      {
        id: "opsAudit",
        label: "Audit",
        options: opsAuditOptions,
        selectedValues: opsAuditFilters,
        onSelectionChange: onOpsAuditFiltersChange,
      },
    ];
  }, [
    filterOptions,
    configuredStateFilters,
    onConfiguredStateFiltersChange,
    sectionFilters,
    onSectionFiltersChange,
    preparerFilters,
    onPreparerFiltersChange,
    opsAuditFilters,
    onOpsAuditFiltersChange,
  ]);

  return (
    <EntityFilterBar
      searchTerm={searchTerm}
      onSearchTermChange={onSearchTermChange}
      searchPlaceholder="Search work steps by name or description..."
      categories={filterCategories}
      isFiltersOpen={isFiltersOpen}
      onFiltersOpenChange={onFiltersOpenChange}
      activeFilterCount={activeFilterCount}
      hasActiveFilters={hasActiveFilters}
      onClearFilters={onClearFilters}
      entityType="work_steps"
    />
  );
};
